<script lang="ts" setup>
import { GoogleLogin } from 'vue3-google-login'
import type { CallbackTypes } from 'vue3-google-login'

defineProps({
  block: {
    default: false,
    type: Boolean,
  },
  size: {
    default: 'sm',
    type: String as PropType<AuthGoogleConnectSize>,
  },
})

const emit = defineEmits<{
  authenticated: []
}>()

const { createEvent } = useAnalytics()
const { createToast } = useToasts()
const { locale, t } = useI18n()
const authStore = useAuthStore()
const config = useRuntimeConfig()
const localePath = useLocalePath()
const route = useRoute()
const router = useRouter()

const loggingIn = ref(false)

const sizes = {
  lg: 'xl',
  sm: 'lg',
} as const

function onGoogleClick() {
  if (!loggingIn.value) {
    loggingIn.value = true
  }
}

async function onGoogleAuthenticating(response: CallbackTypes.CodePopupResponse) {
  try {
    const { isNew, user } = await authStore.loginWithGoogle({
      code: response.code,
      invitationToken: (route.query?.invitationToken || '') as string,
      locale: locale.value as Locale,
    })

    createToast({
      i18nParams: { fullName: user.fullName || '' },
      message: `authWelcome${user.fullName ? 'FullName' : ''}`,
    })

    createEvent({
      name: `[Auth] ${isNew ? 'Register' : 'Login'}`,
      properties: { Platform: 'google' },
    })

    emit('authenticated')

    if (String(route.name).startsWith('auth-login')) {
      router.push(localePath('companies'))
    }
  }
  catch (error) {
    console.error(error)
  }

  loggingIn.value = false
}

function onGoogleAuthenticationError() {
  loggingIn.value = false
}
</script>

<template>
  <ClientOnly>
    <GoogleLogin
      :callback="onGoogleAuthenticating"
      :class="{ 'w-full': block }"
      :client-id="config.public.googleAuth.clientId"
      :error="onGoogleAuthenticationError"
    >
      <UButton
        block
        color="white"
        :loading="loggingIn"
        :size="sizes[size]"
        @click="onGoogleClick"
      >
        <svg
          v-if="!loggingIn"
          height="24"
          viewBox="0 0 24 24"
          width="24"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
            fill="#4285F4"
          />

          <path
            d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
            fill="#34A853"
          />

          <path
            d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
            fill="#FBBC05"
          />

          <path
            d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
            fill="#EA4335"
          />

          <path
            d="M1 1h22v22H1z"
            fill="none"
          />
        </svg>

        {{ t('continueWithGoogle') }}
      </UButton>
    </GoogleLogin>
  </ClientOnly>
</template>

<i18n lang="json">
{
  "en": {
    "continueWithGoogle": "Continue with Google"
  },
  "es": {
    "continueWithGoogle": "Continuar con Google"
  },
  "fr": {
    "continueWithGoogle": "Continuer avec Google"
  }
}
</i18n>
